import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useReducedMotionPreferred } from '../../../hooks/useReducedMotionPreferred';

/**
 * HeroCarousel
 * Slides through images with translate transform.
 * Single image: no nav, no dots.
 *
 * @param {string[]} images
 * @param {string} alt
 * @param {(index: number) => void} onImageClick
 * @param {{ xs: number, md: number }} [height]
 */
function HeroCarousel({ images, alt, onImageClick, height = { xs: 250, md: 350 } }) {
  const [index, setIndex] = useState(0);
  const reducedMotion = useReducedMotionPreferred();

  const count = images.length;

  const handlePrev = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev - 1 + count) % count);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev + 1) % count);
  };

  const handleDot = (idx) => {
    setIndex(idx);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        overflow: 'hidden',
        bgcolor: 'grey.900',
      }}
    >
      {/* Sliding strip */}
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          transform: `translateX(-${index * 100}%)`,
          transition: reducedMotion ? 'none' : 'transform 0.5s ease-in-out',
        }}
      >
        {images.map((src, i) => (
          <Box
            key={i}
            component="img"
            src={src}
            alt={`${alt} ${i + 1}`}
            onClick={() => onImageClick && onImageClick(i)}
            onError={(e) => {
              e.currentTarget.src =
                'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            }}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              flexShrink: 0,
              cursor: onImageClick ? 'pointer' : 'default',
            }}
          />
        ))}
      </Box>

      {/* Prev / Next buttons — only when multiple images */}
      {count > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous image"
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: 2,
              '&:hover': { bgcolor: 'common.white' },
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={handleNext}
            aria-label="Next image"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: 2,
              '&:hover': { bgcolor: 'common.white' },
            }}
          >
            <ChevronRight />
          </IconButton>

          {/* Pill-expander dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {images.map((_, i) => (
              <Box
                key={i}
                role="button"
                aria-label={`Image ${i + 1}`}
                onClick={() => handleDot(i)}
                sx={{
                  width: i === index ? 24 : 8,
                  height: 8,
                  borderRadius: '4px',
                  bgcolor: i === index ? 'common.white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: reducedMotion ? 'none' : 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

export default HeroCarousel;
